

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ArrowsDownUpIcon,
  CheckIcon,
  EyeSlashIcon,
  FunnelIcon,
  PencilSimpleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { ColumnFilterValue } from '../types';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserAvatar } from '@/components/ui/user-avatar';

interface ColumnMenuPopoverProps {
  columnId: string;
  columnLabel: string;
  fieldType?: string;
  filterValue?: ColumnFilterValue;
  onFilterChange?: (columnId: string, value: ColumnFilterValue | null) => void;
  // Column actions
  canHide?: boolean;
  canSort?: boolean;
  canFilter?: boolean;
  canEdit?: boolean; // Whether this column's field definition can be edited
  canRename?: boolean; // Whether this column's display label can be renamed inline
  customFieldDefinitionId?: string; // ID of the custom field definition (for edit functionality)
  onHideColumn?: (columnId: string) => void;
  onToggleSort?: (columnId: string) => void;
  onEditField?: (customFieldDefinitionId: string) => void;
  onRename?: (columnId: string, newLabel: string) => void;
  // Trigger element
  children: ReactNode;
  // Options for DROPDOWN and USER fields
  dropdownOptions?: Array<{ value: string; label: string }>;
  userOptions?: Array<{ id: string; name: string; email?: string }>;
  // Callback when popover open state changes
  onOpenChange?: (open: boolean) => void;
  triggerClassName?: string;
  triggerTitle?: string;
}

export function ColumnMenuPopover({
  columnId,
  columnLabel,
  fieldType = 'STRING',
  filterValue,
  onFilterChange,
  canHide = true,
  canSort = true,
  canFilter = true,
  canEdit = false,
  canRename = false,
  customFieldDefinitionId,
  onHideColumn,
  onToggleSort,
  onEditField,
  onRename,
  children,
  dropdownOptions = [],
  userOptions = [],
  onOpenChange,
  triggerClassName,
  triggerTitle,
}: ColumnMenuPopoverProps) {
  const [open, setOpen] = useState(false);

  // Notify parent when open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };
  const [showFilter, setShowFilter] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameValue, setRenameValue] = useState(columnLabel);
  const [localValue, setLocalValue] = useState<ColumnFilterValue | null>(filterValue || null);
  // Search term for DROPDOWN and USER filters - must be at top level to follow Rules of Hooks
  const [filterSearchTerm, setFilterSearchTerm] = useState('');

  // Sync local value with external filter value
  useEffect(() => {
    setLocalValue(filterValue || null);
    // Also reset showFilter when filter is cleared externally
    if (!filterValue || filterValue.value === null || filterValue.value === undefined ||
        (Array.isArray(filterValue.value) && filterValue.value.length === 0)) {
      setShowFilter(false);
    }
  }, [filterValue]);

  useEffect(() => {
    if (open && filterValue) {
      setShowFilter(true);
    }
    // Reset search term when popover closes
    if (!open) {
      setFilterSearchTerm('');
      setShowRename(false);
    }
  }, [open, filterValue]);

  const handleApply = () => {
    if (localValue && localValue.value !== null && localValue.value !== '' && 
        (!Array.isArray(localValue.value) || localValue.value.length > 0)) {
      onFilterChange(columnId, localValue);
    } else {
      onFilterChange(columnId, null);
    }
  };

  const handleClear = () => {
    setLocalValue(null);
    onFilterChange(columnId, null);
    setShowFilter(false);
  };

  const handleSort = () => {
    if (onToggleSort) {
      onToggleSort(columnId);
      handleOpenChange(false);
    }
  };

  const handleHide = () => {
    if (onHideColumn) {
      onHideColumn(columnId);
      handleOpenChange(false);
    }
  };

  const handleEdit = () => {
    if (onEditField && customFieldDefinitionId) {
      onEditField(customFieldDefinitionId);
      handleOpenChange(false);
    }
  };

  const openRenameView = () => {
    setRenameValue(columnLabel);
    setShowRename(true);
  };

  const handleRenameSave = () => {
    if (!onRename) return;
    onRename(columnId, renameValue.trim());
    setShowRename(false);
    handleOpenChange(false);
  };

  const toggleFilterView = () => {
    setShowFilter(!showFilter);
  };

  const hasActiveFilter = filterValue && filterValue.value !== null && filterValue.value !== '' &&
    (!Array.isArray(filterValue.value) || filterValue.value.length > 0);

  const renderFilterContent = () => {
    switch (fieldType) {
      case 'NUMBER':
      case 'CURRENCY': {
        const numValue = localValue?.value as { min?: number; max?: number } | undefined;
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="min-value" className="text-xs text-muted-foreground">Min</Label>
                <Input
                  id="min-value"
                  type="number"
                  placeholder="0"
                  className="h-8"
                  value={numValue?.min ?? ''}
                  onChange={(e) => {
                    const min = e.target.value ? parseFloat(e.target.value) : undefined;
                    setLocalValue({
                      type: 'number',
                      value: { ...(numValue || {}), min }
                    });
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="max-value" className="text-xs text-muted-foreground">Max</Label>
                <Input
                  id="max-value"
                  type="number"
                  placeholder="999"
                  className="h-8"
                  value={numValue?.max ?? ''}
                  onChange={(e) => {
                    const max = e.target.value ? parseFloat(e.target.value) : undefined;
                    setLocalValue({
                      type: 'number',
                      value: { ...(numValue || {}), max }
                    });
                  }}
                />
              </div>
            </div>
          </div>
        );
      }

      case 'BOOLEAN': {
        const boolValue = localValue?.value as boolean | null | undefined;
        return (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button
                variant={boolValue === true ? 'default' : 'outline'}
                size="sm"
                className="flex-1 h-8"
                onClick={() => setLocalValue({ type: 'boolean', value: true })}
              >
                <CheckIcon className="h-3 w-3 mr-1" />
                Yes
              </Button>
              <Button
                variant={boolValue === false ? 'default' : 'outline'}
                size="sm"
                className="flex-1 h-8"
                onClick={() => setLocalValue({ type: 'boolean', value: false })}
              >
                <XIcon className="h-3 w-3 mr-1" />
                No
              </Button>
            </div>
          </div>
        );
      }

      case 'DATE':
      case 'DATETIME': {
        const dateValue = localValue?.value as { min?: string; max?: string } | undefined;
        return (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="from-date" className="text-xs text-muted-foreground">From</Label>
              <Input
                id="from-date"
                type="date"
                className="h-8"
                value={dateValue?.min ?? ''}
                onChange={(e) => {
                  const min = e.target.value || undefined;
                  setLocalValue({
                    type: 'date',
                    value: { ...(dateValue || {}), min }
                  });
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="to-date" className="text-xs text-muted-foreground">To</Label>
              <Input
                id="to-date"
                type="date"
                className="h-8"
                value={dateValue?.max ?? ''}
                onChange={(e) => {
                  const max = e.target.value || undefined;
                  setLocalValue({
                    type: 'date',
                    value: { ...(dateValue || {}), max }
                  });
                }}
              />
            </div>
          </div>
        );
      }

      case 'DROPDOWN':
      case 'MULTISELECT': {
        const selectedValues = (localValue?.value as string[]) || [];

        const filteredOptions = dropdownOptions.filter(option =>
          option.label.toLowerCase().includes(filterSearchTerm.toLowerCase())
        );

        return (
          <div className="space-y-2">
            {dropdownOptions.length > 5 && (
              <Input
                type="text"
                placeholder="Search options..."
                className="h-8"
                value={filterSearchTerm}
                onChange={(e) => setFilterSearchTerm(e.target.value)}
              />
            )}
            <ScrollArea className="h-48">
              <div className="space-y-0.5 pr-3">
                {filteredOptions.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No options found
                  </div>
                ) : (
                  filteredOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent transition-colors text-left",
                          isSelected && "bg-accent/50"
                        )}
                        onClick={() => {
                          const newValues = isSelected
                            ? selectedValues.filter(v => v !== option.value)
                            : [...selectedValues, option.value];
                          setLocalValue({
                            type: 'select',
                            value: newValues
                          });
                        }}
                      >
                        <div className={cn(
                          "h-4 w-4 border rounded flex items-center justify-center flex-shrink-0",
                          isSelected && "bg-primary border-primary"
                        )}>
                          {isSelected && <CheckIcon className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <span className="truncate">{option.label}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
            {selectedValues.length > 0 && (
              <div className="text-xs text-muted-foreground pt-1 border-t">
                {selectedValues.length} selected
              </div>
            )}
          </div>
        );
      }

      case 'USER':
      case 'MULTI_USER': {
        const selectedUsers = (localValue?.value as string[]) || [];

        const filteredUsers = userOptions.filter(user =>
          user.name.toLowerCase().includes(filterSearchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(filterSearchTerm.toLowerCase())
        );

        return (
          <div className="space-y-2">
            {userOptions.length > 5 && (
              <Input
                type="text"
                placeholder="Search users..."
                className="h-8"
                value={filterSearchTerm}
                onChange={(e) => setFilterSearchTerm(e.target.value)}
              />
            )}
            <ScrollArea className="h-48">
              <div className="space-y-0.5 pr-3">
                {filteredUsers.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No users found
                  </div>
                ) : (
                  filteredUsers.map((user) => {
                    const isSelected = selectedUsers.includes(user.id);
                    return (
                      <button
                        key={user.id}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent transition-colors text-left",
                          isSelected && "bg-accent/50"
                        )}
                        onClick={() => {
                          const newUsers = isSelected
                            ? selectedUsers.filter(id => id !== user.id)
                            : [...selectedUsers, user.id];
                          setLocalValue({
                            type: 'user',
                            value: newUsers
                          });
                        }}
                      >
                        <div className={cn(
                          "h-4 w-4 border rounded flex items-center justify-center flex-shrink-0",
                          isSelected && "bg-primary border-primary"
                        )}>
                          {isSelected && <CheckIcon className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <UserAvatar
                          user={{ id: user.id, name: user.name, email: user.email }}
                          size="xs"
                          className="shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{user.name}</div>
                          {user.email && <div className="text-xs text-muted-foreground truncate">{user.email}</div>}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
            {selectedUsers.length > 0 && (
              <div className="text-xs text-muted-foreground pt-1 border-t">
                {selectedUsers.length} selected
              </div>
            )}
          </div>
        );
      }

      case 'STRING':
      case 'TEXT':
      case 'RICH_TEXT':
      default: {
        const textValue = localValue?.value as string | undefined;
        return (
          <div className="space-y-1.5">
            <Input
              type="text"
              placeholder={`Search ${columnLabel.toLowerCase()}...`}
              className="h-8"
              value={textValue ?? ''}
              onChange={(e) => {
                setLocalValue({
                  type: 'text',
                  value: e.target.value
                });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleApply();
                  handleOpenChange(false);
                }
              }}
              autoFocus
            />
          </div>
        );
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger className={triggerClassName} title={triggerTitle}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          // Don't auto-focus to prevent immediate keyboard events
          e.preventDefault();
        }}
      >
        {showRename ? (
          // Rename view - edit the column's display label
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowRename(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <XIcon className="h-4 w-4" />
              </button>
              <h4 className="font-medium text-sm">Rename column</h4>
            </div>

            <Input
              value={renameValue}
              maxLength={60}
              placeholder={columnLabel}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRenameSave();
                }
              }}
              autoFocus
            />

            <div className="flex gap-2 pt-2">
              <Button size="sm" onClick={handleRenameSave}>
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowRename(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : !showFilter ? (
          // Menu view - show column actions
          <div className="py-1">
            {canSort && onToggleSort && (
              <button
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                onClick={handleSort}
              >
                <ArrowsDownUpIcon className="h-4 w-4" />
                <span>Sort</span>
              </button>
            )}
            
            {canFilter && onFilterChange && (
              <button
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                onClick={toggleFilterView}
              >
                <FunnelIcon className="h-4 w-4" />
                <span>Filter</span>
                {hasActiveFilter && (
                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
                )}
              </button>
            )}

            {canRename && onRename && (
              <>
                {(canSort || (canFilter && onFilterChange)) && <Separator className="my-1" />}
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                  onClick={openRenameView}
                >
                  <PencilSimpleIcon className="h-4 w-4" />
                  <span>Rename column</span>
                </button>
              </>
            )}

            {canEdit && customFieldDefinitionId && onEditField && (
              <>
                {(canSort || (canFilter && onFilterChange) || (canRename && onRename)) && <Separator className="my-1" />}
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                  onClick={handleEdit}
                >
                  <PencilSimpleIcon className="h-4 w-4" />
                  <span>Edit field</span>
                </button>
              </>
            )}

            {canHide && onHideColumn && (
              <>
                {(canSort || (canFilter && onFilterChange) || (canEdit && customFieldDefinitionId) || (canRename && onRename)) && <Separator className="my-1" />}
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                  onClick={handleHide}
                >
                  <EyeSlashIcon className="h-4 w-4" />
                  <span>Hide column</span>
                </button>
              </>
            )}
          </div>
        ) : (
          // Filter view
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleFilterView}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <XIcon className="h-4 w-4" />
                </button>
                <h4 className="font-medium text-sm">Filter by {columnLabel}</h4>
              </div>
              {hasActiveFilter && (
                <div className="h-2 w-2 rounded-full bg-primary" title="Filter active" />
              )}
            </div>

            {renderFilterContent()}

            <div className="flex gap-2 pt-2">
              <Button
                size="sm"
                onClick={() => {
                  handleApply();
                  handleOpenChange(false);
                }}
                className="flex-1 h-8"
              >
                Apply
              </Button>
              {hasActiveFilter && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleClear}
                  className="h-8"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

ColumnMenuPopover.displayName = "ColumnMenuPopover";
